import {skipToken} from '@reduxjs/toolkit/query';
import {Attributes, showNotification} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {
  AiVehicleServiceIntervalsResponseBody,
  useApplyAiVehicleServiceIntervalMutation,
} from '@dms/api/vehicle';
import {useAnnualMileageQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  Nullish,
  EMPTY_PLACEHOLDER,
  isNotNilOrEmpty,
} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {VehicleServiceIntervalCommentary} from './VehicleServiceIntervalCommentary';
import {VehicleServiceIntervalFluidAttributes} from './VehicleServiceIntervalFluidAttributes';
import {VehicleServiceIntervalPlan} from './VehicleServiceIntervalPlan';
import {VehicleServiceIntervalReplacementAttributes} from './VehicleServiceIntervalReplacementAttributes';

interface VehicleServiceIntervalsAIAttributesProps extends RequiredTestIdProps {
  vehicleData: GetVehicleApiResponseV2 | Nullish;
  record?: AiVehicleServiceIntervalsResponseBody | Nullish;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function VehicleServiceIntervalsAIAttributes(
  props: VehicleServiceIntervalsAIAttributesProps
) {
  const formatDateTime = useDateTimeFormatter();

  const vehicleId = props.vehicleData?.id;

  const annualMileageQuery = useAnnualMileageQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);

  const [applyAiVehicleServiceInterval, {isLoading: isApplying}] =
    useApplyAiVehicleServiceIntervalMutation();

  const intervals = props.vehicleData?.serviceIntervals;
  const suggestions = props.record?.suggestions;
  const recordId = props.record?.id;

  // Once the suggestions have been applied to the vehicle (backend stamps `appliedAt`), the current
  // values already reflect the plan, so the apply action is no longer offered.
  const isApplied = isNotNil(props.record?.appliedAt);
  const canApply = isNotNil(suggestions) && isNotNil(recordId) && !isApplied;

  const aiServiceInspection = suggestions?.serviceInspection;
  const aiEngineOil = suggestions?.engineOil;
  const aiGearboxOil = suggestions?.gearboxOil;
  const aiFourByFourOilDistributor = suggestions?.fourByFourOilDistributor;
  const aiTimingBelt = suggestions?.timingBelt;
  const aiBrakeFluid = suggestions?.brakeFluid;
  const aiCoolantFluid = suggestions?.coolantFluid;

  const serviceInspection = intervals?.serviceInspection;

  // Apply writes the record's suggestions into the vehicle's actual service intervals on the
  // backend; the refreshed vehicle (Vehicle cache tag is invalidated) updates the current column.
  const handleApply = () => {
    if (isNil(recordId) || isNil(vehicleId)) {
      return;
    }

    applyAiVehicleServiceInterval({aiVehicleServiceIntervalId: recordId, vehicleId})
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <Show when={isNotNilOrEmpty(props.record?.plan?.commentary)}>
        <VehicleServiceIntervalCommentary
          commentary={props.record?.plan?.commentary}
          data-testid={suffixTestId('commentary', props)}
        />
      </Show>
      <VehicleServiceIntervalPlan
        plan={props.record?.plan}
        onApply={canApply ? handleApply : undefined}
        isApplyLoading={isApplying}
        data-testid={suffixTestId('plan', props)}
      />
      <HStack spacing={4}>
        <Box width="50%" />
        <Box flex={1}>
          <HStack justify="space-between">
            <Text size="xxSmall" color="secondary">
              {i18n.t('entity.vehicle.labels.currentValue')}
            </Text>
            <Text size="xxSmall" color="secondary">
              {i18n.t('entity.vehicle.labels.aiSuggestion')}
            </Text>
          </HStack>
        </Box>
      </HStack>
      <Heading size={4}>{i18n.t('entity.vehicle.labels.serviceInspection')}</Heading>
      <Attributes
        rows={[
          {
            label: i18n.t('entity.vehicle.labels.dateOfLastInspection'),
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.lastServiceInspectionDate
                    ? formatDateTime(
                        'dateShort',
                        parseDate(serviceInspection.lastServiceInspectionDate)
                      )
                    : EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
          {
            label: i18n.t('entity.vehicle.labels.replacementAfterMonths'),
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.replacementAfterMonths ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{aiServiceInspection?.afterMonths ?? EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
          {
            label: i18n.t('entity.vehicle.labels.dateOfNextServiceInspection'),
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.nextServiceInspectionDate
                    ? formatDateTime(
                        'dateShort',
                        parseDate(serviceInspection.nextServiceInspectionDate)
                      )
                    : EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">
                  {aiServiceInspection?.nextDate
                    ? formatDateTime('dateShort', parseDate(aiServiceInspection.nextDate))
                    : EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            ),
          },
          {
            label: `${i18n.t('entity.vehicle.labels.lastServiceInspection')} (${i18n.t('general.metric.km')})`,
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.lastServiceInspectionMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
          {
            label: `${i18n.t('entity.vehicle.labels.replacementAfterMileage')} (${i18n.t('general.metric.km')})`,
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.replacementAfterMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{aiServiceInspection?.afterMileage ?? EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
          {
            label: `${i18n.t('entity.vehicle.labels.nextServiceInspection')} (${i18n.t('general.metric.km')})`,
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.nextServiceInspectionMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{aiServiceInspection?.nextMileage ?? EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
        ]}
        data-testid={suffixTestId('serviceInspection', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.engineOil')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={intervals?.engineOil}
        aiPart={aiEngineOil}
        data-testid={suffixTestId('engineOil', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.gearboxOil')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={intervals?.gearboxOil}
        aiPart={aiGearboxOil}
        data-testid={suffixTestId('gearboxOil', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.fourByFourOilDistributor')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={intervals?.fourByFourOilDistributor}
        aiPart={aiFourByFourOilDistributor}
        data-testid={suffixTestId('fourByFourOilDistributor', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.timingBelt')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={intervals?.timingBelt}
        aiPart={aiTimingBelt}
        data-testid={suffixTestId('timingBelt', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.brakeFluid')}</Heading>
      <VehicleServiceIntervalFluidAttributes
        part={intervals?.brakeFluid}
        aiPart={aiBrakeFluid}
        data-testid={suffixTestId('brakeFluid', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.coolantFluid')}</Heading>
      <VehicleServiceIntervalFluidAttributes
        part={intervals?.coolantFluid}
        aiPart={aiCoolantFluid}
        data-testid={suffixTestId('coolantFluid', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.annualMileage')}</Heading>

      <Attributes
        rows={[
          {
            label: `${i18n.t('entity.vehicle.labels.serviceVisits')} (${i18n.t('general.metric.km')})`,

            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {annualMileageQuery.data?.annualMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{props.record?.annualMileageUsed ?? EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
        ]}
        data-testid={suffixTestId('annualMileage', props)}
      />
    </VStack>
  );
}
